import { action } from '@ember/object';
import { service } from '@ember/service';
import type { Owner } from '@ember/test-helpers/build-owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { differenceInDays } from 'date-fns';
import { task } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import config from 'garaje/config/environment';
import type AreaMapModel from 'garaje/models/area-map';
import type AjaxService from 'garaje/services/ajax';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import {
  LIVE_MAP_SHARE_INCLUDE_FIELDS,
  LIVE_MAP_SHARE_OPTIONS,
  LIVE_MAP_SHARE_STRATEGY,
  LIVE_MAP_SHARE_STRATEGY_OPTIONS_MAPPING,
} from 'garaje/utils/enums';
import urlBuilder, { buildUrl } from 'garaje/utils/url-builder';

interface ShareLiveMapModalArgs {
  onClose: () => void;
  areaMap: AreaMapModel;
  mapFloor?: { id: string };
}

type SelectOptionType = {
  label: string;
  value: string;
};

interface mapSharingItem {
  shareId: string;
  url: string;
  config: {
    include: string[];
  };
  expiresAt: string | null;
}

export default class ShareLiveMapModal extends Component<ShareLiveMapModalArgs> {
  @service declare ajax: AjaxService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @tracked sharingInfo: mapSharingItem | undefined;
  @tracked shareStrategy: SelectOptionType | undefined;
  @tracked pendingShareStrategy: SelectOptionType | undefined;
  @tracked sharingUrl: string | undefined;
  @tracked pendingShareUrl: string | undefined;
  @tracked shouldHideSensitiveInfo: boolean = true;
  @tracked shouldShowConfirmationModal: boolean = false;
  @tracked isConfirmingSensitiveInfoChange: boolean = false;

  constructor(owner: Owner, args: ShareLiveMapModalArgs) {
    super(owner, args);
    void this.initializeDataTask.perform();
  }

  initializeDataTask = task({ drop: true }, async () => {
    await this.loadMapSharingUrlTask.perform();
  });

  loadMapSharingUrlTask = task({ restartable: true }, async () => {
    const url = urlBuilder.maps.getMapSharingUrl(this.args.areaMap.id);
    try {
      const response = await this.ajax.request(url, {
        type: 'GET',
        contentType: 'application/vnd.api+json',
        headers: { accept: 'application/vnd.api+json' },
      });
      const data = response as mapSharingItem[];
      if (data.length > 0) {
        this.sharingInfo = data[0];
        if (this.sharingInfo) {
          this.setStateFromResponse(this.sharingInfo);
        }
      } else {
        this.sharingUrl = this.internalSharingUrl;
        this.shareStrategy = LIVE_MAP_SHARE_STRATEGY_OPTIONS_MAPPING.INTERNAL;
      }
    } catch (_e) {
      this.flashMessages.showFlash('error', 'Error fetching map sharing settings');
    }
  });

  createMapSharingUrlTask = task({ restartable: true }, async (hideSensitiveInfo: boolean) => {
    try {
      const url = urlBuilder.maps.getMapSharingUrl(this.args.areaMap.id);
      const includeFields = [];
      if (!hideSensitiveInfo) {
        includeFields.push(LIVE_MAP_SHARE_INCLUDE_FIELDS.SENSITIVE_INFO);
      }
      const response = await this.ajax.request(url, {
        type: 'POST',
        contentType: 'application/vnd.api+json',
        headers: { accept: 'application/vnd.api+json' },
        data: JSON.stringify({
          data: {
            type: 'map-sharing',
            attributes: {
              config: {
                include: includeFields,
              },
            },
          },
        }),
      });
      const data = response as mapSharingItem;
      this.sharingInfo = data;
      if (this.sharingInfo) {
        this.setStateFromResponse(this.sharingInfo);
      }
    } catch (_e) {
      this.flashMessages.showFlash('error', 'Error creating map sharing url');
    }
  });

  deleteMapSharingUrlTask = task({ restartable: true }, async () => {
    const url = urlBuilder.maps.deleteMapSharingUrl(this.sharingInfo?.shareId as string);
    try {
      await this.ajax.request(url, {
        type: 'DELETE',
      });
    } catch (_e) {
      this.flashMessages.showFlash('error', 'Error deleting map sharing url');
    }
  });

  setStateFromResponse(data: mapSharingItem): void {
    this.sharingUrl = data.url;
    this.shareStrategy = LIVE_MAP_SHARE_STRATEGY_OPTIONS_MAPPING.EXTERNAL;
    this.shouldHideSensitiveInfo = !data.config.include.includes(LIVE_MAP_SHARE_INCLUDE_FIELDS.SENSITIVE_INFO);
  }

  get internalSharingUrl(): string {
    const floorId = this.args.mapFloor?.id ?? this.args.areaMap.id;
    return buildUrl(`/spaces/maps/live/${floorId}`, config.host);
  }

  get shareStrategyOptions(): SelectOptionType[] {
    return LIVE_MAP_SHARE_OPTIONS;
  }

  get shouldShowSensitiveInfoToggle(): boolean {
    const externalShareStrategy = LIVE_MAP_SHARE_STRATEGY_OPTIONS_MAPPING.EXTERNAL;
    return this.shareStrategy?.value === externalShareStrategy?.value;
  }

  get modalTitle(): string {
    if (this.shouldShowConfirmationModal) {
      return 'Update sharing settings';
    } else {
      return 'Share live map';
    }
  }

  get shouldShowExpirationText(): boolean {
    return this.shouldShowSensitiveInfoToggle && this.sharingInfo?.expiresAt != null;
  }

  get expirationText(): string {
    const currentDate = new Date();
    const expirationDate = new Date(this.sharingInfo?.expiresAt as string);
    // +1 because the expiration date rounds down from current time
    const daysUntil = differenceInDays(expirationDate, currentDate) + 1;

    if (daysUntil <= 0) {
      return 'Link expired';
    }
    return `This link expires in ${pluralize(daysUntil, 'day')}`;
  }

  regenerateSharingInfoTask = task({ drop: true }, async () => {
    await this.deleteMapSharingUrlTask.perform();
    await this.createMapSharingUrlTask.perform(this.shouldHideSensitiveInfo);
  });

  @action
  async onConfirmLinkRegeneration(): Promise<void> {
    if (this.pendingShareStrategy?.value === LIVE_MAP_SHARE_STRATEGY.INTERNAL) {
      try {
        await this.deleteMapSharingUrlTask.perform();
        this.shareStrategy = this.pendingShareStrategy;
        this.sharingUrl = this.pendingShareUrl;
      } catch (_e) {
        this.flashMessages.showFlash('error', 'Error switching sharing permissions');
      }
    } else if (this.isConfirmingSensitiveInfoChange) {
      try {
        this.shouldHideSensitiveInfo = !this.shouldHideSensitiveInfo;
        this.metrics.trackEvent('EXTERNAL_LIVE_MAP_SHARE_LINK_PIN_TOGGLED', {
          showPii: !this.shouldHideSensitiveInfo,
        });
        void this.regenerateSharingInfoTask.perform();
      } catch (_e) {
        this.flashMessages.showFlash('error', 'Error switching sensitive information permissions');
      }
    }
    this.resetPendingState();
  }

  @action
  onShareStrategyChange(option: SelectOptionType): void {
    if (option.value === LIVE_MAP_SHARE_STRATEGY.EXTERNAL) {
      this.shouldHideSensitiveInfo = true;
      this.metrics.trackEvent('EXTERNAL_LIVE_MAP_SHARE_LINK_CREATED');
      void this.createMapSharingUrlTask.perform(this.shouldHideSensitiveInfo);
      this.shareStrategy = option;
    } else {
      this.pendingShareStrategy = option;
      this.pendingShareUrl = this.internalSharingUrl;
      this.shouldShowConfirmationModal = true;
    }
  }

  @action
  toggleHideSensitiveInfo(): void {
    this.isConfirmingSensitiveInfoChange = true;
    this.shouldShowConfirmationModal = true;
  }

  @action
  resetPendingState(): void {
    this.pendingShareStrategy = undefined;
    this.pendingShareUrl = undefined;
    this.isConfirmingSensitiveInfoChange = false;
    this.shouldShowConfirmationModal = false;
  }
}
